/**
 * CSV citizenship export — full-file export with configured columns + last_modified.
 */

import type { Item } from '../../types/prioritization/lifecycle';
import type { CsvCitizenshipProfileMeta } from './csvCitizenshipTypes';
import type { FieldMappingEntry } from '../fieldMappingRegistry';

const DEFAULT_EXPORT_COLUMNS = [
  'external_id',
  'title',
  'description',
  'itemCategory',
  'altitudeLevel',
  'work_type',
  'launch_type',
  'investment_type',
  'tags',
  'source',
] as const;

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function itemField(item: Item, key: string): string {
  if (key === 'last_modified' || key === 'updatedAt') {
    const d = item.updatedAt instanceof Date ? item.updatedAt : new Date(item.updatedAt as string);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  if (key === 'createdAt') {
    const d = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt as string);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  if (key === 'tags') {
    return Array.isArray(item.tags) ? item.tags.join(', ') : '';
  }
  const rec = item as unknown as Record<string, unknown>;
  const val = rec[key];
  if (val == null) return '';
  if (Array.isArray(val)) return val.join(', ');
  if (val instanceof Date) return val.toISOString();
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

export function resolveExportColumns(
  meta: CsvCitizenshipProfileMeta,
  entries: FieldMappingEntry[],
): Array<{ internalKey: string; header: string }> {
  const exportEntries = entries.filter(
    (e) => e.enabled && (e.direction === 'export' || e.direction === 'both'),
  );
  let keys =
    meta.columnOrder && meta.columnOrder.length > 0
      ? [...meta.columnOrder]
      : exportEntries.length > 0
        ? exportEntries.map((e) => e.internalFieldKey)
        : [...DEFAULT_EXPORT_COLUMNS];

  keys = keys.filter((k) => k !== 'last_modified');
  keys.push('last_modified');

  return keys.map((internalKey) => ({
    internalKey,
    header: meta.columnHeaders?.[internalKey] ?? internalKey,
  }));
}

export function filterItemsForExport(
  items: Item[],
  meta: CsvCitizenshipProfileMeta,
): Item[] {
  const filter = meta.sourceFilter ?? { type: 'all' as const };
  if (filter.type === 'all') return items;
  if (filter.type === 'active') {
    return items.filter((i) => !i.exit_status || i.exit_status === 'active');
  }
  if (filter.type === 'collection' && filter.collectionId) {
    // Collection membership may live on item.collections or metadata — soft filter
    return items.filter((i) => {
      const cols = (i as { collectionIds?: string[] }).collectionIds;
      if (Array.isArray(cols)) return cols.includes(filter.collectionId);
      return true; // if unknown, include (honest: filter is best-effort without join)
    });
  }
  return items;
}

export function buildCitizenshipExportCsv(
  items: Item[],
  meta: CsvCitizenshipProfileMeta,
  entries: FieldMappingEntry[],
): { csv: string; rowCount: number; columns: string[] } {
  const filtered = filterItemsForExport(items, meta);
  const columns = resolveExportColumns(meta, entries);
  const headerLine = columns.map((c) => escapeCsvCell(c.header)).join(',');
  const lines = filtered.map((item) =>
    columns.map((c) => escapeCsvCell(itemField(item, c.internalKey))).join(','),
  );
  return {
    csv: [headerLine, ...lines].join('\n') + '\n',
    rowCount: filtered.length,
    columns: columns.map((c) => c.header),
  };
}
